// Global lobby chat stays open for an authenticated session so messages and
// unread state keep flowing while the player is in a waiting room or match.
import { PROTOCOL_VERSION } from '/shared/protocol.js';
import { net, ui } from '../engine/state.js';
import {
  LOBBY_CHAT_FEED_ID,
  setLobbyPresenceCount,
  resetChatChannel,
  applyChatHistory,
  appendChatEntry,
  setChatAdmin,
  showChatNotice,
  chatBlockedNoticeText,
  removeChatLineById,
  clearChatMessages
} from '../ui/chat.js';

// Injected rather than imported: ui/lobby.js already imports this module for
// setLobbyChatDesired, so importing it back would close a cycle.
let onLobbyRoomsPushed = null;

function setLobbyRoomsHandler(handler) {
  onLobbyRoomsPushed = typeof handler === 'function' ? handler : null;
}

const RECONNECT_DELAY_MS = 3000;

const state = {
  socket: null,
  desired: false,
  connected: false,
  reconnectTimer: 0
};

function makeLobbyWsUrl() {
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${window.location.host}/ws-lobby`;
}

function clearReconnectTimer() {
  if (state.reconnectTimer) {
    clearTimeout(state.reconnectTimer);
    state.reconnectTimer = 0;
  }
}

function scheduleReconnect() {
  if (!state.desired || state.reconnectTimer) return;
  state.reconnectTimer = setTimeout(() => {
    state.reconnectTimer = 0;
    if (state.desired) openSocket();
  }, RECONNECT_DELAY_MS);
}

function handleEvent(message) {
  if (message?.kind === 'lobby_welcome') {
    setChatAdmin(Boolean(message?.data?.admin));
    resetChatChannel(LOBBY_CHAT_FEED_ID);
    setLobbyPresenceCount(message?.data?.online);
    applyChatHistory(Array.isArray(message?.data?.history) ? message.data.history : [], LOBBY_CHAT_FEED_ID);
    return;
  }
  if (message?.kind === 'lobby_rooms') {
    onLobbyRoomsPushed?.(message?.data?.rooms);
    return;
  }
  if (message?.kind === 'lobby_presence') {
    setLobbyPresenceCount(message?.data?.online);
    return;
  }
  if (message?.kind === 'chat') {
    appendChatEntry(message.data, LOBBY_CHAT_FEED_ID);
    return;
  }
  if (message?.kind === 'chat_blocked') {
    showChatNotice(chatBlockedNoticeText(message?.data?.reason, message?.data?.retryInMs), LOBBY_CHAT_FEED_ID);
    return;
  }
  if (message?.kind === 'chat_deleted') {
    removeChatLineById(message?.data?.id, LOBBY_CHAT_FEED_ID);
    return;
  }
  if (message?.kind === 'chat_cleared') {
    clearChatMessages(LOBBY_CHAT_FEED_ID);
    return;
  }
  if (message?.kind === 'chat_notice') {
    if (typeof message?.data?.text === 'string') {
      showChatNotice(message.data.text, LOBBY_CHAT_FEED_ID);
    }
    return;
  }
  if (message?.kind === 'lobby_banned') {
    showChatNotice('You are banned from global chat.', LOBBY_CHAT_FEED_ID, { force: true });
    state.desired = false;
    closeSocket();
    ui.controlsDirty = true;
  }
}

function openSocket() {
  if (state.socket) return;
  let socket;
  try {
    socket = new WebSocket(makeLobbyWsUrl());
  } catch (_) {
    scheduleReconnect();
    return;
  }
  state.socket = socket;

  socket.addEventListener('open', () => {
    if (state.socket !== socket) return;
    state.connected = true;
    // The chat input's enabled state is refreshed lazily; wake it up.
    ui.controlsDirty = true;
    socket.send(JSON.stringify({
      t: 'hello',
      v: PROTOCOL_VERSION,
      room: 'lobby',
      name: net.playerName || 'Player'
    }));
  });

  socket.addEventListener('message', (event) => {
    if (state.socket !== socket) return;
    let message = null;
    try {
      message = JSON.parse(event.data);
    } catch (_) {
      return;
    }
    if (message?.t === 'e') {
      handleEvent(message);
    }
  });

  const onGone = () => {
    if (state.socket !== socket) return;
    state.socket = null;
    state.connected = false;
    ui.controlsDirty = true;
    scheduleReconnect();
  };
  socket.addEventListener('close', onGone);
  socket.addEventListener('error', onGone);
}

function closeSocket() {
  clearReconnectTimer();
  const socket = state.socket;
  state.socket = null;
  state.connected = false;
  if (!socket) return;
  try {
    if (socket.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify({ t: 'bye' }));
    }
    socket.close(1000, 'leaving lobby');
  } catch (_) {
  }
}

// Single sync point for the authenticated lifetime of global chat.
function setLobbyChatDesired(desired) {
  const next = Boolean(desired);
  if (state.desired === next) return;
  state.desired = next;
  if (next) {
    openSocket();
  } else {
    closeSocket();
  }
}

function isLobbyChatConnected() {
  return state.connected;
}

function sendLobbyChat(text) {
  const trimmed = typeof text === 'string' ? text.trim().slice(0, 180) : '';
  if (!trimmed || !state.connected || !state.socket) return false;
  try {
    state.socket.send(JSON.stringify({ t: 'c', text: trimmed }));
    return true;
  } catch (_) {
    return false;
  }
}

function sendLobbyChatDelete(id) {
  const trimmed = typeof id === 'string' ? id.trim() : '';
  if (!trimmed || !state.connected || !state.socket) return false;
  try {
    state.socket.send(JSON.stringify({ t: 'cd', id: trimmed }));
    return true;
  } catch (_) {
    return false;
  }
}

export {
  setLobbyRoomsHandler,
  LOBBY_CHAT_FEED_ID,
  isLobbyChatConnected,
  sendLobbyChat,
  sendLobbyChatDelete,
  setLobbyChatDesired
};
